import { useForm, FormProvider } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { useEffect, useCallback } from 'react'
import { useCreateIncubation } from './api'
import { formSchema } from './schema'
import FormBuilder from '../../../../components/app/formBuilder'

import {
  ContactInformation,
  personalInformation,
  addressInformation,
  aboutStartup,
  incubationNeeds,
} from './PreFill'
import UploadCv from './Upload'

interface DataPrefillerProps {
  data?: any
}

// Utility Functions
const formatDate = (date: any) => {
  if (!date) return ''
  return new Date(date).toISOString().split('T')[0]
}

const formatWebsite = (url: string) => {
  if (!url) return ''
  return url.startsWith('http://') || url.startsWith('https://')
    ? url
    : `https://${url}`
}

const downloadFile = (filePath: string) => {
  if (!filePath) return
  const fullUrl = `https://edc-staging.inovace.in/api/${filePath}`
  const fileName = filePath.split('/').pop() || 'file'
  const link = document.createElement('a')
  link.href = fullUrl
  link.download = fileName
  link.target = '_blank'
  link.rel = 'noopener noreferrer'
  document.body.appendChild(link)
  link.click()
  document.body.removeChild(link)
}

// Individual Download Handlers
const downloadSignatureFile = (path: string) => downloadFile(path)
const downloadPitchDeckAdmin = (path: string) => downloadFile(path)
const downloadPitchDeckUser = (path: string) => downloadFile(path)
const downloadOfferLetter = (path: string) => downloadFile(path)
const downloadMou = (path: string) => downloadFile(path)

export default function DataPrefiller({ data }: DataPrefillerProps) {
  const methods = useForm({
    resolver: zodResolver(formSchema),
    mode: 'onChange',
    defaultValues: data || {},
  })

  useEffect(() => {
    methods.reset({
      ...data,
    })
  }, [data, methods])

  const onSuccess = useCallback(() => {
    methods.reset()
  }, [methods])

  const { mutate } = useCreateIncubation(onSuccess)
  const { handleSubmit } = methods

  function getStatusText(status: string): string {
    switch (status) {
      case 'submitted':
        return 'Submitted'
      case 'screening':
        return 'Screening'
      case 'shortlisted':
        return 'Shortlisted'
      case 'interview_scheduled':
        return 'Interview Scheduled'
      case 'rejected':
        return 'Rejected'
      case 'selected':
        return 'Selected'
      case 'offer_sent':
        return 'Offer Sent'
      case 'accepted':
        return 'Accepted'
      case 'space_allotted':
        return 'Space Allotted'
      default:
        return 'Unknown Status'
    }
  }
  function getStatusColor(status: string): string {
    switch (status) {
      case 'submitted':
        return 'text-blue-600 bg-blue-100'
      case 'screening':
        return 'text-yellow-600 bg-yellow-100'
      case 'shortlisted':
        return 'text-purple-600 bg-purple-100'
      case 'interview_scheduled':
        return 'text-indigo-600 bg-indigo-100'
      case 'rejected':
        return 'text-red-600 bg-red-100'
      case 'selected':
        return 'text-green-600 bg-green-100'
      case 'offer_sent':
        return 'text-cyan-600 bg-cyan-100'
      case 'accepted':
        return 'text-emerald-600 bg-emerald-100'
      case 'space_allotted':
        return 'text-lime-600 bg-lime-100'
      default:
        return 'text-gray-600 bg-gray-100'
    }
  }

  const onSubmit = useCallback(
    (data: any) => {
      const formData = new FormData()

      // Required fields
      formData.append('startup_name', data.startup_name)
      formData.append('legal_entity_type', data.legal_entity_type)
      formData.append('registration_date', formatDate(data.registration_date))
      formData.append('website', formatWebsite(data.website))
      formData.append('contact_name', data.contact_name)
      formData.append('contact_designation', data.contact_designation)
      formData.append('contact_email', data.contact_email)
      formData.append('contact_phone', data.contact_phone)
      formData.append('address_street', data.address_street)
      formData.append('address_city', data.address_city)
      formData.append('address_state', data.address_state)
      formData.append('address_zip', data.address_zip)
      formData.append('address_country', data.address_country)

      // Optional fields
      const optionalFields = [
        'industry_sector',
        'startup_stage',
        'business_description',
        'annual_turnover',
        'problem_statement',
        'solution',
        'unique_value_proposition',
        'target_market',
        'revenue_model',
        'service_interest',
        'resources_needed',
        'key_milestones',
        'previous_programs',
        'additional_comments',
        'signature',
      ]

      optionalFields.forEach((field) => {
        if (data[field]) formData.append(field, data[field])
      })

      formData.append('declaration_agreed', data.declaration_agreed ? '0' : '1')

      if (data.declaration_date) {
        formData.append('declaration_date', formatDate(data.declaration_date))
      }

      mutate(formData)
    },
    [mutate]
  )

  return (
    <>
      <div className="w-11/12 mx-auto ">
        <div className="flex justify-end items-center gap-5 w-full">
          {' '}
          <div className="text-successColor font-bold">Current Status</div>
          <div>
            <div
              className={` font-semibold px-4 py-1 rounded 
      ${getStatusColor(data.status)} 
      border-current`}
            >
              {getStatusText(data.status)}
            </div>
          </div>
        </div>
      </div>
      <div className="w-11/12 mx-auto py-10">
        <FormProvider {...methods}>
          <form onSubmit={handleSubmit(onSubmit)} className="space-y-10">
            <div className="bg-white p-8 rounded-lg">
              {/* Contact Information */}
              <div className="mb-8">
                <h2 className="text-lg font-semibold">Contact Information</h2>
                <h4 className="text-md font-semibold mt-3">
                  Startup Information
                </h4>
                <p className="text-LightGray text-xs">
                  Please Fill Out All Required Fields
                </p>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <FormBuilder data={ContactInformation} />
                </div>
              </div>

              {/* Personal Information */}
              <div className="mb-8">
                <h3 className="font-semibold text-base">
                  Personal Information
                </h3>
                <p className="text-LightGray text-xs mb-3">
                  Please Fill Out All Required Fields
                </p>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <FormBuilder data={personalInformation} />
                </div>
              </div>

              {/* Registered Address */}
              <div className="mb-8">
                <h3 className="font-semibold text-base">Registered Address</h3>
                <p className="text-LightGray text-xs mb-3">
                  Please Fill Out All Required Fields
                </p>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <FormBuilder data={addressInformation} />
                </div>
              </div>

              {/* About Your Startup */}
              <div className="mb-8">
                <h2 className="text-lg font-semibold mb-6">
                  About Your Startup
                </h2>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <FormBuilder data={aboutStartup} />
                </div>
              </div>

              {/* Incubation Needs & Expectations */}
              <div className="mb-8">
                <h2 className="text-lg font-semibold mb-6">
                  Incubation Needs & Expectations
                </h2>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <FormBuilder data={incubationNeeds} />
                </div>
              </div>

              {/* Download Buttons */}
              {data?.signature && (
                <div className="mt-6">
                  <h3 className="font-semibold text-base mb-2">Signature</h3>
                  <button
                    type="button"
                    onClick={() => downloadSignatureFile(data.signature)}
                    className="px-4 py-2 bg-linear-orange text-white rounded hover:bg-blue-600 transition-colors"
                  >
                    Download Signature
                  </button>
                </div>
              )}

              {data?.pitch_deck_admin && (
                <div className="mt-6">
                  <h3 className="font-semibold text-base mb-2">
                    Pitch deck sample from admin
                  </h3>
                  <button
                    type="button"
                    onClick={() =>
                      downloadPitchDeckAdmin(data.pitch_deck_admin)
                    }
                    className="px-4 py-2 bg-linear-orange text-white rounded hover:bg-blue-600 transition-colors"
                  >
                    Download Pitch Deck (Admin)
                  </button>
                </div>
              )}

              {data?.pitch_deck_user && (
                <div className="mt-6">
                  <h3 className="font-semibold text-base mb-2">
                    Uploaded Pitch deck
                  </h3>
                  <button
                    type="button"
                    onClick={() => downloadPitchDeckUser(data.pitch_deck_user)}
                    className="px-4 py-2 bg-linear-orange text-white rounded hover:bg-blue-600 transition-colors"
                  >
                    Download Pitch Deck (User)
                  </button>
                </div>
              )}

              {data?.offer_letter_path && (
                <div className="mt-6">
                  <h3 className="font-semibold text-base mb-2">Offer Letter</h3>
                  <button
                    type="button"
                    onClick={() => downloadOfferLetter(data.offer_letter_path)}
                    className="px-4 py-2 bg-linear-orange text-white rounded hover:bg-blue-600 transition-colors"
                  >
                    Download Offer Letter
                  </button>
                </div>
              )}

              {data?.mou_path && (
                <div className="mt-6">
                  <h3 className="font-semibold text-base mb-2">MOU</h3>
                  <button
                    type="button"
                    onClick={() => downloadMou(data.mou_path)}
                    className="px-4 py-2 bg-linear-orange text-white rounded hover:bg-blue-600 transition-colors"
                  >
                    Download MOU
                  </button>
                </div>
              )}
            </div>
          </form>
        </FormProvider>
      </div>
      <UploadCv data={data} />
    </>
  )
}

//
